<template>
  <v-form @submit.prevent="submitForm" ref="formRef" v-model="formValid">
    <v-card class="px-5 py-3">
      <v-card-title class="d-flex justify-space-between align-center mb-3">
        <span>{{ formTitle }}</span>
      </v-card-title>

      <v-card-text>
        <div v-if="!fields?.length">
          <v-alert type="warning" border="start" prominent>
            No hay campos definidos para este formulario.
          </v-alert>
        </div>

        <v-row v-else>
          <template v-for="field in fields" :key="field.value">
            <v-col :cols="12" :md="field.col || 12" :sm="field.mdcol || 12">
              <component
                v-if="!field.isJsonArray"
                :is="field.component"
                v-model="form[field.value]"
                v-bind="getFieldProps(field)"
                :rules="field.required ? [v => !!v || 'Campo obligatorio'] : []"
              />
              <component
                v-else
                :is="field.component"
                v-model="form[field.value]"
                :fields="field.children"
                :label="field.label"
                :summary="field.summary"
                :multiple="multiple"
              />
            </v-col>
          </template>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="cancelForm">Cancelar</v-btn>
        <v-btn type="submit" color="secondary" :disabled="!formValid">Confirmar</v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { FormField } from '@/types/form';

const { fields, formTitle, onSubmit, onCancel } = defineProps<{
  fields: FormField[];
  formTitle?: string;
  onSubmit: (data: Record<string, any>) => void;
  onCancel?: () => void;
}>();

const form = ref<Record<string, any>>({});
const formRef = ref();
const formValid = ref(true);

// Inicializa el form
fields.forEach((f) => {
  if (f.isJsonArray) {
    form.value[f.value] = [{}]; // array de un objeto vacío
  } else {
    form.value[f.value] = '';
  }
});


const getFieldProps = (field: FormField) => {
  const props: Record<string, any> = {
    label: field.label,
    placeholder: field.placeholder ?? '',
    clearable: true,
    density: 'compact',
    type: field.type || 'text',
    prefix: field.prefix,
    suffix: field.suffix,
    'prepend-inner-icon': field.icon,
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
  };

  if (field.options) {
    props.items = field.options;
    props['item-title'] = field.itemText || 'text';
    props['item-value'] = field.itemValue || 'value';
  }

  return props;
};

const submitForm = () => {
  if (!formRef.value?.validate()) return;
  onSubmit({ ...form.value });
};

const cancelForm = () => {
  onCancel?.();
};
</script>
